/**
 * System — der Zustand der ganzen Installation.
 *
 * **Nur für den Plattform-Admin.** Wer hierherkommt, sucht eine Störung an der
 * Installation: Antwortet die Datenbank, kommen Gerätewerte an, läuft der
 * Worker. Das ist die Frage dessen, der den Server betreibt, nicht die eines
 * Nutzers, der seinen Verbrauch ansehen will — dem sagt die Marke in der
 * Kopfzeile bereits, ob die Anwendung ihre API erreicht. Der Schutz liegt
 * ohnehin beim Server; hier wird nur nicht angeboten, was niemandem hilft.
 *
 * **Bis HM-062 zeigte diese Seite zwei Zeilen** — Postgres und ClickHouse —,
 * weil sie den öffentlichen `GET /health` spiegelte. Von zehn Diensten der
 * Installation fehlten damit acht, darunter der Worker, dessen Ausfall
 * überhaupt niemandem aufgefallen wäre. Die Auskunft kommt jetzt aus
 * `GET /admin/system`; der Health-Endpunkt bleibt, was er ist: die Antwort für
 * den Reverse Proxy.
 *
 * **Vier Zustände, nicht drei.** `unknown` ist der wichtigste davon: Ein Dienst,
 * der sich noch nie gemeldet hat, ist nicht ausgefallen. Er bekommt deshalb
 * Grau und nicht Rot — dieselbe Entscheidung wie bei `never_seen` an einem
 * frisch gekoppelten Gerät (HM-042): Wer eine rote Marke einmal zu Unrecht
 * gesehen hat, glaubt der nächsten nicht mehr.
 *
 * **Die Messkonzept-Vorlagen standen hier und sind wieder heraus.** Sie sind ein
 * Katalog aus der Fachlogik, den man weder ändern noch anwenden kann, solange
 * es keine Messkonzepte gibt (HM-012).
 */
import { Badge, Button, Card } from "@hausmeter/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ApiComponents } from "@hausmeter/sdk";
import { api, apiBaseUrl } from "../lib/client.js";
import { formatTimeAgo } from "../lib/format.js";
import { listRow, mutedText, plainList, rowLabel } from "../lib/list-styles.js";
import { useSystemStatus } from "../lib/queries.js";

type ComponentState = ApiComponents["schemas"]["ComponentState"];
type SystemComponent = ApiComponents["schemas"]["SystemComponent"];
type MailCheck = ApiComponents["schemas"]["MailCheckResponse"];

/**
 * Gruppiert wird nach der **Frage**, die jemand hat, und nicht nach der Technik
 * dahinter — dieselbe Entscheidung wie auf der Gerätedetailseite (HM-041). Wer
 * hierherkommt, fragt „kommen meine Werte an", nicht „läuft Mosquitto".
 */
const GRUPPEN: readonly { key: SystemComponent["group"]; title: string; hint: string }[] = [
  { key: "anwendung", title: "Anwendung", hint: "Was Anfragen beantwortet und im Hintergrund rechnet." },
  { key: "datenhaltung", title: "Datenhaltung", hint: "Wo Stammdaten, Zeitreihen und Dateien liegen." },
  { key: "geraete", title: "Geräte", hint: "Der Weg, auf dem Messwerte hereinkommen." },
  { key: "zustellung", title: "Zustellung", hint: "Wie Nachrichten an Menschen hinausgehen." },
];

export function SystemScreen() {
  const status = useSystemStatus();
  const queryClient = useQueryClient();

  const mailCheck = useMutation<MailCheck>({
    mutationFn: async () => {
      const { data, error } = await api.POST("/admin/system/mail-check", {});
      if (error || !data) throw new Error("Die Prüfung ließ sich nicht ausführen.");
      return data;
    },
    /**
     * **Die Zeile kommt vom Server, nicht aus dieser Antwort.** Der Server merkt
     * sich das Ergebnis; würde die Oberfläche es selbst hinmalen, sähe ein
     * zweiter Admin weiter „nicht feststellbar" — und ein Neuladen dieser Seite
     * auch.
     */
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["system-status"] }),
  });

  return (
    <>
      <Card
        title="Zustand der Installation"
        subtitle={
          status.data
            ? `Geprüft ${formatTimeAgo(status.data.observedAt)}`
            : undefined
        }
        action={status.data ? <StateBadge state={status.data.status} /> : undefined}
      >
        {status.isPending && <p style={mutedText}>Wird geprüft …</p>}

        {status.isError && (
          <p style={{ ...mutedText, color: "var(--hm-danger)" }}>
            Die API unter <code>{apiBaseUrl}</code> ist nicht erreichbar.
          </p>
        )}

        {status.data && (
          <>
            {/* **Welcher Stand läuft hier?** Die Frage nach jedem Ausrollen — und
                die Version allein beantwortet sie nicht: Zwischen zwei Fassungen
                liegen viele Commits, und ausgerollt wird auch dazwischen. */}
            <ul style={plainList}>
              <li style={listRow}>
                <span style={{ ...rowLabel, minWidth: "min(150px, 100%)" }}>Fassung</span>
                <span style={mutedText}>
                  {status.data.build.version}
                  {status.data.build.commit !== null && ` · ${status.data.build.commit}`}
                  {status.data.build.builtAt !== null &&
                    ` · gebaut ${formatTimeAgo(status.data.build.builtAt)}`}
                </span>
              </li>
            </ul>
            {status.data.build.commit === null && (
              <p style={mutedText}>
                Dieser Stand trägt keine Commit-Kennung — er wurde nicht über{" "}
                <code>deploy-prod.sh</code> gebaut. Welcher Code hier läuft, lässt sich damit
                nicht sagen.
              </p>
            )}
            <p style={mutedText}>
              <strong>Nicht feststellbar</strong> ist nicht dasselbe wie ausgefallen. Ein Dienst,
              der sich noch nie gemeldet hat, steht grau da — grün behauptete etwas, das niemand
              geprüft hat, und rot einen Ausfall, den es nicht gibt.
            </p>
          </>
        )}
      </Card>

      {status.data &&
        GRUPPEN.map((gruppe) => {
          const bausteine = status.data.components.filter((c) => c.group === gruppe.key);
          // Eine leere Karte erscheint gar nicht: Sie sagt nichts und sieht aus
          // wie ein Schaden.
          if (bausteine.length === 0) return null;

          return (
            <Card key={gruppe.key} title={gruppe.title} subtitle={gruppe.hint}>
              <ul style={plainList}>
                {bausteine.map((baustein) => (
                  <li key={baustein.name} style={{ ...listRow, alignItems: "flex-start" }}>
                    <StateBadge state={baustein.state} />
                    <span style={{ ...rowLabel, minWidth: "min(150px, 100%)" }}>{baustein.label}</span>
                    <span style={{ ...mutedText, flex: 1 }}>
                      {baustein.detail ?? "—"}
                      {baustein.latencyMs !== null && ` · ${baustein.latencyMs} ms`}
                      {baustein.lastSeenAt !== null &&
                        ` · zuletzt ${formatTimeAgo(baustein.lastSeenAt)}`}
                    </span>

                    {/* Der Mailversand hat keinen laufenden Zustand — er wird
                        ausgelöst. Der Knopf steht deshalb an seiner Zeile und
                        nicht als weitere Karte darunter. */}
                    {baustein.name === "mail" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => mailCheck.mutate()}
                        disabled={mailCheck.isPending}
                      >
                        {mailCheck.isPending ? "Prüft …" : "Versand prüfen"}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>

              {/* **Das Ergebnis steht in der Zeile oben, nicht hier.** Hier steht
                  nur, was der Knopf tut — sonst liest jemand die Zeile, sieht
                  „nicht feststellbar" und hält die Prüfung für kaputt. Und ein
                  Fehlschlag *der Anfrage* ist etwas anderes als ein Mailserver,
                  der nicht antwortet: Den zweiten trägt die Zeile. */}
              {gruppe.key === "zustellung" && mailCheck.isError && (
                <p style={{ ...mutedText, marginTop: 10, color: "var(--hm-danger)" }}>
                  Die Prüfung ließ sich nicht ausführen — die Anfrage kam nicht durch. Das sagt
                  nichts über den Mailserver.
                </p>
              )}
              {gruppe.key === "zustellung" && !mailCheck.isError && (
                <p style={{ ...mutedText, marginTop: 10 }}>
                  Die Prüfung baut eine echte Verbindung zum Mailserver auf und meldet sich an.
                  <strong> Verschickt wird nichts</strong> — eine Testmail hätte einen Empfänger,
                  und den gibt es hier nicht. Das Ergebnis bleibt oben stehen, bis die API neu
                  startet.
                </p>
              )}
            </Card>
          );
        })}
    </>
  );
}

/**
 * Zustand als Marke — mit Text, nicht nur mit Farbe. Wer Rot und Grün nicht
 * unterscheidet, liest sonst nichts.
 *
 * `unknown` bekommt **neutral** und nicht `warning`: Es ist keine Störung,
 * sondern das Eingeständnis, es nicht zu wissen.
 */
function StateBadge({ state }: { state: ComponentState }) {
  const tone =
    state === "ok" ? "success" : state === "degraded" ? "warning" : state === "down" ? "danger" : "neutral";
  const label =
    state === "ok"
      ? "läuft"
      : state === "degraded"
        ? "eingeschränkt"
        : state === "down"
          ? "ausgefallen"
          : "nicht feststellbar";
  return (
    <Badge tone={tone} title={state}>
      {label}
    </Badge>
  );
}
